import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Sparkle, ArrowRight, ListChecks } from '@phosphor-icons/react';

import { askCoach } from '@/components/coach/cards';

// GreetingHeader — the top of the daily command center: a time-aware greeting
// with the admin's first name, today's date, an at-a-glance to-do count (links
// to the assistant), and the "ask coach" launcher. Sits over a soft pastel blob
// (rendered by HomeDashboard).
export function GreetingHeader({ slug, todoCount }: { slug: string; todoCount: number }) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const profile = auth.user?.profile;
  const firstName =
    (profile?.given_name as string | undefined) ??
    (profile?.name as string | undefined)?.split(' ')[0] ??
    '';

  const hour = new Date().getHours();
  const partOfDay = hour < 5 ? 'night' : hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const greeting = firstName
    ? t(`home.greeting.${partOfDay}Named`, { name: firstName })
    : t(`home.greeting.${partOfDay}`);

  const today = new Intl.DateTimeFormat(i18n.language || undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date());

  return (
    <header className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight md:text-3xl">
            {greeting}
          </h1>
          <p className="text-sm capitalize text-muted-foreground md:text-base">{today}</p>
        </div>
        <div className="flex items-center gap-2">
          {todoCount > 0 && (
            <Link
              to={`/orgs/${slug}/assistant`}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-pill bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground transition hover:scale-[1.04] motion-reduce:hover:scale-100"
            >
              <ListChecks size={16} weight="bold" aria-hidden />
              {t('coach.dashboard.todoCount', { count: todoCount })}
            </Link>
          )}
          <button
            type="button"
            onClick={() => askCoach(t('coach.dashboard.askNextPrompt'))}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-pill border border-border bg-card px-4 py-2 text-sm font-semibold transition hover:scale-[1.04] motion-reduce:hover:scale-100"
          >
            <Sparkle size={16} weight="fill" aria-hidden />
            {t('coach.dashboard.askNext')}
            <ArrowRight size={16} weight="bold" aria-hidden />
          </button>
        </div>
      </div>
    </header>
  );
}
